import { authFetch } from './authFetch';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fetch all normalized tracks from the backend
export async function fetchNormalizedTracks() {
  const res = await authFetch(`${BACKEND_URL}/tracks/normalized/`);
  if (!res.ok) throw new Error('Failed to fetch normalized tracks');
  return await res.json(); // { track_name: {inside: [...], outside: [...]}, ... }
}

// Normalize a lap path (center, scale, resample)
export function normalizeLapPath(lapArr, numPoints = 200) {
  if (!lapArr || lapArr.length === 0) return [];
  // Convert to [x, y] with y = -position_z
  const path = lapArr.map(p => [p.position_x, -p.position_z]);
  // Resample
  const dists = [0];
  for (let i = 1; i < path.length; i++) {
    const dx = path[i][0] - path[i-1][0];
    const dy = path[i][1] - path[i-1][1];
    dists.push(dists[dists.length-1] + Math.sqrt(dx*dx + dy*dy));
  }
  const total = dists[dists.length-1];
  if (total === 0) return Array(numPoints).fill(path[0]);
  const target = Array.from({length: numPoints}, (_, i) => i * total / (numPoints-1));
  const resampled = target.map(t => {
    let idx = dists.findIndex(d => d >= t);
    if (idx === -1) return path[path.length-1];
    if (idx === 0) return path[0];
    const frac = (t - dists[idx-1]) / (dists[idx] - dists[idx-1]);
    return [
      path[idx-1][0] + frac * (path[idx][0] - path[idx-1][0]),
      path[idx-1][1] + frac * (path[idx][1] - path[idx-1][1])
    ];
  });
  // Center and scale
  const meanX = resampled.reduce((a, b) => a + b[0], 0) / numPoints;
  const meanY = resampled.reduce((a, b) => a + b[1], 0) / numPoints;
  const centered = resampled.map(([x, y]) => [x - meanX, y - meanY]);
  const maxDist = Math.max(...centered.map(([x, y]) => Math.hypot(x, y)));
  const scaled = maxDist > 0 ? centered.map(([x, y]) => [x / maxDist, y / maxDist]) : centered;
  return scaled;
}

// Compute mean squared error between two paths
export function pathMSE(pathA, pathB) {
  if (!pathA || !pathB || pathA.length !== pathB.length) return Infinity;
  let sum = 0;
  for (let i = 0; i < pathA.length; i++) {
    const dx = pathA[i][0] - pathB[i][0];
    const dy = pathA[i][1] - pathB[i][1];
    sum += dx*dx + dy*dy;
  }
  return sum / pathA.length;
}

// Find the best matching track for a lap
export function detectTrackForLap(lapArr, normalizedTracks, mseThreshold = 0.01) {
  const lapNorm = normalizeLapPath(lapArr);
  let best = null;
  let bestMSE = Infinity;
  for (const [trackName, borders] of Object.entries(normalizedTracks)) {
    if (!borders || !borders.inside) continue;
    const mse = pathMSE(lapNorm, borders.inside);
    if (mse < bestMSE) {
      bestMSE = mse;
      best = trackName;
    }
  }
  if (bestMSE < mseThreshold) return best;
  return null;
}
